import { createAsyncThunk } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { getOrganizationId, isJson } from "Libs/utils";
import { selectFeatureOrganizationsEnabled } from "Reducers/featureFlags/featureFlags.selectors";

import { getOrganization } from "./getOrganization.thunk";

import type {
  LoadOrganizationProps,
  LoadOrganizationPayloadFulfilled
} from "../types";
import type { Organization } from "@packages/client";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadOrganization = createAsyncThunk<
  LoadOrganizationPayloadFulfilled,
  LoadOrganizationProps,
  AsyncThunkOptionType
>(
  "app/organizations/load_organization",
  async (
    { organizationDescriptionId },
    { rejectWithValue, getState, dispatch }
  ) => {
    const organizationEnabled = selectFeatureOrganizationsEnabled(getState());
    if (!organizationEnabled) {
      return;
    }

    const orgResponse = await dispatch(
      getOrganization({ organizationDescriptionId, shouldUpdateState: true })
    );

    let organization: Organization | undefined =
      orgResponse?.payload?.organization;

    if (organization) {
      return;
    }

    try {
      const platformLib = await import("Libs/platform");
      const client = platformLib.default;

      let organizationId = getOrganizationId(
        getState,
        organizationDescriptionId
      );

      // For admins, they may not have the organization in their /organizations response
      if (!organizationId) {
        organizationId = `name=${organizationDescriptionId}`;
      }

      try {
        organization = await client.getOrganization(organizationId);
      } catch (err) {
        if (isJson(err)) {
          const errorAsJSON = JSON.parse(err);
          // The user needs either MFA or SSO to access the organization
          if (errorAsJSON.error === "insufficient_user_authentication") {
            // We need to patch the error since we don't have enough information
            // from the client.js
            errorAsJSON.status = 401;

            return rejectWithValue(errorAsJSON);
          }
        }
        throw "organization.notfound";
      }
      return organization;
    } catch (err) {
      if (![404, 403, 400].includes((err as { code: number }).code)) {
        logger(err, {
          action: "loadOrganization",
          id: organizationDescriptionId
        });
      }
      return rejectWithValue(err);
    }
  }
);
